import React, { useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import process from 'node:process';
import { match } from 'variant';
import { useDebugLog } from '@wix/cli-debug-log';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { ASTRO_APP_CONFIG_PATH } from '@wix/cli-astro-definitions';
import { useAsync, useExit, Spinner } from '@wix/cli-ui-kit';
import { readAppConfig, saveAppConfig } from '../../../../files/app.config.js';
import { useProjectModel } from '../../../../model.js';
import { ResolveSite } from './ResolveSite.js';
import { SiteContext } from './context.js';
import { SiteValue } from './site-value.js';

export const SiteProvider: FC<{ children: (siteId: string) => ReactNode }> = ({
  children,
}) => {
  const exit = useExit();
  const { model } = useProjectModel();
  const { projectFolder, env } = model;
  const userDefinedSiteId = env.WIX_SITE_ID;
  const writeDebugLog = useDebugLog();

  const { status, merge } = useAsync(async () => {
    if (userDefinedSiteId) {
      return SiteValue.Resolved({ siteId: userDefinedSiteId });
    }
    const siteId = (await readAppConfig(projectFolder, writeDebugLog))?.siteId;

    if (
      !siteId &&
      (process.stdin.isTTY !== true || process.stdout.isTTY !== true)
    ) {
      throw new CliError({
        code: CliErrorCode.DevelopmentSiteNotConfigured(),
        cause: null,
      });
    }

    return siteId ? SiteValue.Resolved({ siteId }) : SiteValue.NotConfigured();
  }, []);

  const setSiteId = useCallback(
    async (siteId: string) => {
      if (userDefinedSiteId) {
        throw new Error('Cannot change site: siteId is user-defined');
      }
      try {
        await saveAppConfig(projectFolder, { siteId });
        merge({ result: SiteValue.Resolved({ siteId }) });
      } catch (e) {
        throw new CliError({
          code: CliErrorCode.FailedToSaveDevelopmentSite({
            path: ASTRO_APP_CONFIG_PATH,
          }),
          cause: e,
        });
      }
    },
    [merge, projectFolder, userDefinedSiteId]
  );

  const handleResolveSite = useCallback(
    (siteId: string) => setSiteId(siteId).catch(exit),
    [exit, setSiteId]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text="Resolving development site..." />,
    Success: ({ result: state }) => {
      return match(state, {
        NotConfigured: () => <ResolveSite onSubmit={handleResolveSite} />,
        Resolved: ({ siteId }) => (
          <SiteContext.Provider
            value={{
              siteId,
              setSiteId,
              canChangeSite: !userDefinedSiteId,
            }}
          >
            {children(siteId)}
          </SiteContext.Provider>
        ),
      });
    },
  });
};
